import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useTenancy } from '@/contexts/TenancyContext';
import * as React from 'react';
import { useState, useEffect } from 'react';
import { getCompanyInfo } from '@/services/companyService';

// DED-605: Helper to fetch business units from customerOrg.teams.businessUnitNames
const getSharedBusinessUnits = async (companyId: string): Promise<string[]> => {
  try {
    const companyInfo = await getCompanyInfo(companyId);
    if (companyInfo?.customerOrg?.teams?.businessUnitNames) {
      const businessUnitNames = companyInfo.customerOrg.teams.businessUnitNames as Record<string, { name: string }>;
      return Object.values(businessUnitNames)
        .filter(bu => bu && bu.name)
        .map(bu => bu.name);
    }
    return [];
  } catch (error) {
    console.error('Error fetching shared business units:', error);
    return [];
  }
};

const defaultMenus = {
  "documentLibrary": {
    "documentTypes": [
      "Policy", 
      "Procedure", 
      "Form", 
      "Evidence", 
      "Matrix", 
      "Checklist", 
      "Standard", 
      "Confluence Page"
    ],
    "classifications": ["Customer Confidential", "Business Confidential", "Public"],
    "businessUnits": ["Management", "Operations", "Development", "Finance"]
  },
  "updatedAt": {
    "__time__": "2025-06-12T03:36:01.984Z"
  }
}

/**
 * Fetches menu settings from Firestore
 * @param companyId - The company ID
 * @param menuType - The type of menu to fetch (e.g., 'documentLibrary', 'risks')
 * @returns Promise that resolves to the requested menu settings
 */
export const getMenuSettings = async (companyId: string, menuType: string) => {
  try {
    const menusRef = doc(db, 'customers', companyId, 'companySettings', 'menus');
    const menusSnap = await getDoc(menusRef);
    
    let menuData = {};
    
    if (menusSnap.exists() && menusSnap.data()) {
      const data = menusSnap.data();
      // Return the specific menu type if it exists, otherwise return an empty object
      menuData = data[menuType] || {};
    } else if (defaultMenus[menuType]) {
      // Check if default menu settings exist
      menuData = defaultMenus[menuType];
    }
    
    // DED-605: For documentLibrary, override businessUnits with shared business units from customerOrg
    if (menuType === 'documentLibrary') {
      const sharedBusinessUnits = await getSharedBusinessUnits(companyId);
      if (sharedBusinessUnits.length > 0) {
        menuData = {
          ...menuData,
          businessUnits: sharedBusinessUnits
        };
      }
    }
    
    if (Object.keys(menuData).length === 0) {
      console.warn(`No menu settings found for ${menuType}, returning empty object`);
    }
    
    return menuData;
  } catch (error) {
    console.error(`Error fetching ${menuType} menu settings:`, error);
    throw error;
  }
};

/**
 * React hook to get menu settings
 * @param menuType - The type of menu to fetch (e.g., 'documentLibrary', 'risks')
 * @returns Object containing menu settings, loading state, and error
 */
export const useMenuSettings = (menuType: string) => {
  const { activeTenancy } = useTenancy();
  const [settings, setSettings] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchMenuSettings = async () => {
      if (!activeTenancy?.id) return;
      
      try {
        setIsLoading(true);
        const data = await getMenuSettings(activeTenancy.id, menuType);
        setSettings(data);
      } catch (err) {
        console.error(`Failed to load ${menuType} menu settings:`, err);
        setError(err instanceof Error ? err : new Error(`Failed to load ${menuType} menu settings`));
      } finally {
        setIsLoading(false);
      }
    };

    fetchMenuSettings();
  }, [activeTenancy?.id, menuType]);

  return { settings, isLoading, error };
};

/**
 * Gets a specific menu option by key
 * @param menuType - The type of menu (e.g., 'documentLibrary')
 * @param optionKey - The key of the option to get (e.g., 'documentTypes', 'statuses')
 * @param companyId - Optional company ID, will use active tenancy if not provided
 * @returns Promise that resolves to the requested menu options array
 */
export const getMenuOptions = async (
  menuType: string,
  optionKey: string,
  companyId?: string
): Promise<Array<{ key: string; value: string }>> => {
  try {
    const effectiveCompanyId = companyId || window.companyID;
    if (!effectiveCompanyId) {
      throw new Error('No company ID provided and none found in window.companyID');
    }

    const menuSettings = await getMenuSettings(effectiveCompanyId, menuType);
    const options = menuSettings[optionKey] || [];
    
    // Convert string array to {key, value} objects if needed
    return Array.isArray(options) 
      ? options.map(opt => typeof opt === 'string' ? { key: opt, value: opt } : opt)
      : [];
  } catch (error) {
    console.error(`Error getting ${optionKey} options:`, error);
    return [];
  }
};

export default {
  getMenuSettings,
  useMenuSettings,
  getMenuOptions
};
